import React from "react";
import SlideOutSidebar from "../../../ui-components/slide-out-sidebar/slideOutSidebar";
import AddressDetails from "./sections/addressDetails";
import { Formik, FormikProps, Form } from "formik";
import Button, { ButtonThemes } from "ui-components/button/button";
import LocationClassification from "./sections/locationClassification";
import { AddEditAddress, AddressDetailsModel, locationTypeOptions, subLocationTypeOptions, validationSchemaForAddress } from "app/data/address-manager/models";
import OperationalRequirements from "./sections/operationalRequirements";
import AddressLookup from "ui-components/address-lookup/addressLookup";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { ERROR_MESSAGES } from "app/data/common/errorMessages";
import AddressmanagerService from "app/data/address-manager/addressService";
import { AddressState } from "slices/address-manager/AddressState";
import { useSelector } from "react-redux";
import { addressSelector } from "slices/address-manager/addressSlice";
import "./addEditAddressSidebar.scss";
import DrivingInstructions from "./sections/drivingInstructions";
import { useVerified } from "../hooks/useVerified";
import { countryOptions } from "app/data/terminal-details/models";

interface AddEditSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    isEdit?: boolean;
    refetchAddressDetails?: () => void;
    refetchAddressCounts?: () => void;
    refetchAddressList?: () => void;
    onDelete?: () => void;
}

const AddEditSidebar: React.FC<AddEditSidebarProps> = ({ 
    isOpen,
    onClose,
    isEdit,
    refetchAddressDetails,
    refetchAddressCounts,
    onDelete,
    refetchAddressList }) => {
    const formRef = React.useRef<FormikProps<AddEditAddress>>(null);
    const { selectedAddress }: AddressState = useSelector(addressSelector);
    const selectedAddressId = selectedAddress?.id;
    const { refetch: refetchVerifiedDetails } = useVerified(selectedAddressId || "");
    const addAddressMutation = useMutation({
        mutationFn: (data: AddressDetailsModel) => {
            return AddressmanagerService.getInstance().createAddress(data)
        },
        onSuccess: () => {
            toast.info("Address added successfully");
            refetchAddressDetails?.();
            refetchAddressCounts?.();
            refetchAddressList?.();
            refetchVerifiedDetails?.();
            onClose?.();
            formRef.current?.resetForm();
        },
        onError: (e: any) => {
            if (e instanceof Error) {
                toast.error(e.message);
            }
            else if (e?.response?.data?.message) {
                toast.error(e.response.data.message);
            }
            else if (typeof e === "string") {
                toast.error(e);
            }
            else {
                toast.error(ERROR_MESSAGES.COMMON);
            }
        },
    });

    const updateAddressMutation = useMutation({
        mutationFn: (data: AddressDetailsModel) => {
            return AddressmanagerService.getInstance().updateAddress(data, selectedAddress?.id)
        },
        onSuccess: () => {
            toast.info("Address updated successfully");
            refetchAddressDetails?.();
            refetchAddressCounts?.();
            refetchVerifiedDetails?.();
        },
        onError: (e: any) => {
            if (e instanceof Error) {
                toast.error(e.message);
            }
            else if (e?.response?.data?.message) {
                toast.error(e.response.data.message);
            }
            else {
                toast.error(ERROR_MESSAGES.COMMON);
            }
        },
    });

    return (
        <SlideOutSidebar
            header={isEdit ? "Edit Address" : "Add New Address"}
            onClose={onClose}
            show={isOpen}
            size="small"
        >
            <Formik
                onSubmit={(values) => {
                    let filteredPayload = {
                        ...Object.fromEntries(
                            Object.entries(values).map(([key, v]) => [
                                key,
                                v === "" ? null : v
                            ])
                        ),
                        limitedAccessSubtype:
                            values.locationType === "Limited Access"
                                ? values.limitedAccessSubtype
                                : null,
                        gateCode: values.gateCodeRequired ? values.gateCode : "",
                    };


                    if (isEdit) {
                        updateAddressMutation.mutate(filteredPayload as AddressDetailsModel);
                    } else {
                        addAddressMutation.mutate(filteredPayload as AddressDetailsModel);
                    }
                }}

                initialValues={
                    {
                        line1: isEdit ? selectedAddress?.line1 : "",
                        line2: isEdit ? (selectedAddress?.line2 ?? "") : "",
                        city: isEdit ? selectedAddress?.city : "",
                        state: isEdit ? selectedAddress?.state : "",
                        zip: isEdit ? selectedAddress?.zip : "",
                        country: isEdit ? selectedAddress?.country : countryOptions.find((option) => option.value === "USA")?.value,
                        timezone: isEdit ? selectedAddress?.timezone : "",
                        locationType: isEdit ? (locationTypeOptions.find((option) => option.value === selectedAddress?.locationType)?.value ?? "") : "",
                        limitedAccessSubtype: isEdit ? (subLocationTypeOptions.find((option) => option.value === selectedAddress?.limitedAccessSubtype)?.value ?? "") : "",
                        areaType: isEdit ? (selectedAddress?.areaType ?? "") : "",
                        facilityType: isEdit ? (selectedAddress?.facilityType ?? "") : "",
                        gateCode: isEdit ? (selectedAddress?.gateCode ?? "") : "",
                        hasDock: isEdit ? (selectedAddress?.hasDock) : false,
                        limitedAccess: isEdit ? (selectedAddress?.limitedAccess) : false,
                        gateCodeRequired: isEdit ? (selectedAddress?.gateCodeRequired) : false,
                        drivingInstructions: isEdit ? (selectedAddress?.drivingInstructions ?? "") : "",
                    }
                }
                validationSchema={validationSchemaForAddress()}
                innerRef={formRef}
                enableReinitialize={true}
            >
                {(formikProps: FormikProps<AddEditAddress>) => (
                    <Form>
                        <div className="add-edit-address-section-container">
                            <div className="add-edit-address-section">
                                <div className="add-edit-address-section__section-title">Address Details</div>
                                <AddressLookup
                                    required={true}
                                    onValueChange={(val) => {
                                        formikProps.setFieldValue("line1", val.address);
                                        formikProps.setFieldValue("city", val.city);
                                        formikProps.setFieldValue("state", val.state);
                                        formikProps.setFieldValue("zip", val.zip);
                                        setTimeout(() => {
                                            formikProps.setFieldTouched("line1", true, true);
                                        }, 100);
                                    }}
                                    onBlur={() => {
                                        formikProps.setFieldTouched("line1", true, true);
                                    }}
                                />

                                <div className="add-edit-address-section__address-details">
                                    <AddressDetails isOpen={isOpen} isEdit={isEdit ?? false}/>
                                </div>
                            </div>
                            <div className="add-edit-address-section">
                                <div className="add-edit-address-section__section-title">Location Classification</div>
                                <LocationClassification isEdit={isEdit ?? false} />
                            </div>
                            <div className="add-edit-address-section">
                                <div className="add-edit-address-section__section-title">Operational Requirements</div>
                                <OperationalRequirements />
                            </div>
                            <div className="add-edit-address-section">
                                <div className="add-edit-address-section__section-title">Driving Instructions</div>
                                <DrivingInstructions />
                            </div>
                        </div>
                        <div className={`add-edit-address-section__footer ${!isEdit ? "add-edit-address-section__flex-end" : ""}`}>
                            {isEdit && <Button
                                type="button"
                                onClick={() => { onDelete?.() }}
                                theme={ButtonThemes.red}
                                className="add-edit-address-section__footer__delete-button"
                                spinner={addAddressMutation.isLoading || updateAddressMutation.isLoading}
                            >
                                Delete
                            </Button>
                            }
                            <div className="add-edit-address-section__footer__gap">
                                <Button
                                    type="button"
                                    onClick={() => onClose()}
                                    theme={ButtonThemes.gray}
                                    className="add-edit-address-section__footer__cancel-button"
                                    spinner={addAddressMutation.isLoading || updateAddressMutation.isLoading}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="button"
                                    onClick={() => formRef.current?.submitForm()}
                                    theme={ButtonThemes.blue}
                                    disabled={!formikProps.dirty}
                                    className="add-edit-address-section__footer__submit-button"
                                    spinner={addAddressMutation.isLoading || updateAddressMutation.isLoading}
                                >
                                    {isEdit ? "Save" : "Add"}
                                </Button>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </SlideOutSidebar>
    );
};

export default AddEditSidebar;
